import type { Store } from '../configureStore.js';
import authSlice, { authInitialState } from '../features/authSlice.js';

import { decodeToken } from './decodeToken.js';

// Minimal store surface the validation needs. Kept structural (not the full
// `Store`) so it can run in the `persistStore` bootstrap callback, before the
// `persistor` is attached to the returned store object.
type AuthValidationStore = Pick<Store, 'getState' | 'dispatch'>;

/**
 * Whether `token` decodes as a JWT carrying a numeric `exp` claim. Mirrors the
 * undecodable-token semantics of `isAccessTokenNearExpiry`: a legacy, pre-SimpleJWT
 * token (e.g. a 40-char hex key) throws inside `decodeToken`, and a token without a
 * numeric `exp` is treated as invalid. This is intentionally NOT `isAccessTokenNearExpiry`
 * itself — a valid-but-near-expiry SimpleJWT token is decodable and must NOT be reset.
 */
const isDecodableJwt = (token: string): boolean => {
  let exp: unknown;
  try {
    ({ exp } = decodeToken(token));
  } catch {
    return false;
  }

  return typeof exp === 'number';
};

/**
 * Belt-and-suspenders validation of the rehydrated auth slice (SPAZ2-3203).
 *
 * After the SimpleJWT deploy a persisted pre-migration session hydrates with a
 * legacy (non-JWT) access token and no refresh token. Refresh is purely reactive
 * (SPAZ2-3301), so such a session would sit authenticated-looking until a request
 * naturally 401s. Validating at hydration sends the user to login without first
 * firing a failing request.
 *
 * The session is treated as invalid — and the auth slice reset to `authInitialState`,
 * which flips the root route guard to unauthorized — when the token is present but
 * either not a decodable JWT or missing its refresh token. An already-empty slice
 * (`token === null`) and a valid SimpleJWT pair are left untouched.
 *
 * Scoped strictly to the auth slice: it does NOT bump the redux-persist `version`
 * or add a `migrate` (either could wipe or transform other slices).
 */
export const validatePersistedSession = (store: AuthValidationStore): void => {
  const { token, refresh } = store.getState().auth;

  if (!token) {
    return;
  }

  if (!refresh || !isDecodableJwt(token)) {
    store.dispatch(authSlice.actions.setAuth(authInitialState));
  }
};

export default validatePersistedSession;
